import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  IconButton,
  ListSubheader,
  Chip,
} from '@mui/material';
import {
  InfoOutlined as InfoOutlinedIcon,
  LocationOn as SiteIcon,
  Sensors as DeploymentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  TIME_SLOTS_24H,
  MOCK_DEPLOYMENT_COMPARISON,
  BioDeployment,
  BioProject,
  DARK_THEME_EXPORTING,
  DARK_THEME_NAVIGATION,
} from '../bioacousticMockData';

interface DeploymentComparisonChartProps {
  project?: BioProject;
  deployments: BioDeployment[];
  height?: number;
}

const METRIC_OPTIONS = [
  { value: 'aci', label: 'ACI', description: 'Acoustic Complexity Index' },
  { value: 'bi', label: 'BI', description: 'Bioacoustic Index' },
  { value: 'adi', label: 'ADI', description: 'Acoustic Diversity Index' },
  { value: 'aei', label: 'AEI', description: 'Acoustic Evenness Index' },
  { value: 'ndsi', label: 'NDSI', description: 'Normalized Difference Soundscape Index' },
];

const COLOR_PALETTE = ['#F59E0B', '#00D4FF', '#8B5CF6', '#10B981', '#EC4899', '#EAB308', '#06B6D4'];
const SYMBOLS: Highcharts.SymbolKeyValue[] = ['circle', 'diamond', 'square', 'triangle', 'triangle-down'];

const DeploymentComparisonChart: React.FC<DeploymentComparisonChartProps> = ({
  project,
  deployments,
  height = 340,
}) => {
  const initialSelected = deployments.slice(0, 2).map((d) => d.deployment_id);
  const [selectedDeploymentIds, setSelectedDeploymentIds] = useState<string[]>(
    initialSelected.length > 0 ? initialSelected : ['dep-1', 'dep-2']
  );
  const [selectedMetric, setSelectedMetric] = useState<string>('aci');

  // Build site lookup mapping with clean short names
  const deploymentToSiteMap = useMemo(() => {
    const map = new Map<string, string>();
    if (project) {
      project.sites.forEach((s) => {
        const shortName = s.site_name.includes('Karpa')
          ? 'Karpa'
          : s.site_name.includes('Masira')
          ? 'Masira'
          : s.site_name.includes('Pipri')
          ? 'Pipri'
          : s.site_name.split(' - ')[1] || s.site_name.split(' ')[0];
        s.deployments.forEach((d) => {
          map.set(d.deployment_id, shortName);
        });
      });
    } else {
      deployments.forEach((d) => {
        map.set(d.deployment_id, 'Site');
      });
    }
    return map;
  }, [project, deployments]);

  // Handle removing a deployment tag
  const handleRemoveDeployment = (depIdToRemove: string) => {
    if (selectedDeploymentIds.length > 1) {
      setSelectedDeploymentIds((prev) => prev.filter((id) => id !== depIdToRemove));
    }
  };

  const chartOptions: Highcharts.Options = useMemo(() => {
    const series: Highcharts.SeriesOptionsType[] = selectedDeploymentIds.map((depId, idx) => {
      const dep = deployments.find((d) => d.deployment_id === depId);
      const siteName = deploymentToSiteMap.get(depId) || '';
      const name = dep ? dep.deployment_name.split(' (')[0] : `Deployment ${idx + 1}`;
      const color = COLOR_PALETTE[idx % COLOR_PALETTE.length];
      const symbol = SYMBOLS[idx % SYMBOLS.length];
      const data = MOCK_DEPLOYMENT_COMPARISON[depId] || MOCK_DEPLOYMENT_COMPARISON['dep-1'];

      return {
        type: 'spline',
        name: siteName ? `[${siteName}] ${name} (${selectedMetric.toUpperCase()})` : `${name} (${selectedMetric.toUpperCase()})`,
        color,
        data,
        marker: {
          symbol,
          radius: 4,
          fillColor: color,
        },
      };
    });

    return {
      chart: {
        type: 'spline',
        backgroundColor: 'transparent',
        height,
        style: {
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
        },
      },
      exporting: DARK_THEME_EXPORTING,
      navigation: DARK_THEME_NAVIGATION,
      title: {
        text: 'Deployment-wise Activity',
        align: 'center',
        style: {
          color: '#FFFFFF',
          fontSize: '15px',
          fontWeight: '700',
          letterSpacing: '0.5px',
        },
      },
      credits: { enabled: false },
      xAxis: {
        categories: TIME_SLOTS_24H,
        title: {
          text: 'Time of Day',
          style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
        },
        labels: {
          rotation: -45,
          style: { color: '#888888', fontSize: '10px' },
        },
        lineColor: '#222222',
        tickColor: '#222222',
        gridLineColor: 'rgba(255, 255, 255, 0.03)',
        crosshair: {
          color: 'rgba(245, 158, 11, 0.3)',
          dashStyle: 'Dash' as any,
        },
      },
      yAxis: {
        title: {
          text: 'Average of Acoustic Indices',
          style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
        },
        labels: {
          style: { color: '#888888', fontSize: '10px' },
        },
        min: 0,
        max: 600,
        tickPositions: [0, 200, 400, 600],
        gridLineColor: '#1A1A1A',
      },
      tooltip: {
        backgroundColor: '#111111',
        borderColor: '#222222',
        style: { color: '#EEEEEE', fontSize: '11px' },
        shared: true,
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          color: '#999999',
          fontSize: '11px',
          fontWeight: '600',
        },
        itemHoverStyle: {
          color: '#FFFFFF',
        },
      },
      series,
    };
  }, [selectedDeploymentIds, selectedMetric, deployments, height, deploymentToSiteMap]);

  return (
    <Box
      sx={{
        background: 'rgba(10, 10, 10, 0.85)',
        border: '1px solid #1E1E1E',
        borderRadius: '12px',
        p: 2.5,
      }}
    >
      {/* Selector Controls bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1.5, borderBottom: '1px solid #1A1A1A' }}>
        {/* Deployments multi-select with Site-grouped hierarchy */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Compare:
          </Typography>
          <Select
            multiple
            variant="standard"
            disableUnderline
            size="small"
            value={selectedDeploymentIds}
            onChange={(e) => {
              const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
              if (val.length > 0) setSelectedDeploymentIds(val);
            }}
            renderValue={(selected) => (
              <Typography sx={{ color: '#F59E0B', fontWeight: 800, fontSize: '12px' }}>
                {selected.length} Deployment(s) Selected
              </Typography>
            )}
            sx={{
              color: '#F59E0B',
              fontWeight: 700,
              fontSize: '12px',
              borderBottom: '2px solid #F59E0B',
              '& .MuiSelect-select': {
                py: 0.25,
                px: 1,
              },
              '& .MuiSvgIcon-root': {
                color: '#F59E0B',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#121212',
                  border: '1px solid #282828',
                  color: '#fff',
                  maxHeight: 340,
                  boxShadow: '0 16px 40px rgba(0,0,0,0.9)',
                  '& .MuiMenuItem-root': {
                    fontSize: '12px',
                    '&.Mui-selected': { bgcolor: 'rgba(245, 158, 11, 0.12)' },
                    '&:hover': { bgcolor: '#1C1C1C' },
                  },
                },
              },
            }}
          >
            {project ? (
              project.sites.map((site) => {
                const shortSite = site.site_name.includes('Karpa')
                  ? 'Karpa'
                  : site.site_name.includes('Masira')
                  ? 'Masira'
                  : site.site_name.includes('Pipri')
                  ? 'Pipri'
                  : site.site_name.split(' - ')[1] || site.site_name.split(' ')[0];

                return [
                  <ListSubheader
                    key={`site-header-${site.site_id}`}
                    sx={{
                      bgcolor: '#181818',
                      color: '#F59E0B',
                      fontWeight: 800,
                      fontSize: '11px',
                      letterSpacing: '0.8px',
                      textTransform: 'uppercase',
                      lineHeight: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      borderTop: '1px solid #242424',
                      borderBottom: '1px solid #242424',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <SiteIcon sx={{ fontSize: 13, color: '#F59E0B' }} />
                      <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#F59E0B' }}>
                        {site.site_name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '10px', color: '#777', fontWeight: 600 }}>
                      {site.deployments.length} stations
                    </Typography>
                  </ListSubheader>,
                  ...site.deployments.map((d) => (
                    <MenuItem
                      key={d.deployment_id}
                      value={d.deployment_id}
                      sx={{
                        pl: 3,
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #181818',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Checkbox
                          checked={selectedDeploymentIds.includes(d.deployment_id)}
                          size="small"
                          sx={{ color: '#555', '&.Mui-checked': { color: '#F59E0B' }, p: 0.25 }}
                        />
                        <DeploymentIcon sx={{ fontSize: 14, color: '#888', flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '12px', color: '#EEE', fontWeight: 600, lineHeight: 1.2 }}>
                            {d.deployment_name}
                          </Typography>
                          <Typography sx={{ fontSize: '10px', color: '#777', lineHeight: 1.1 }}>
                            {d.device_type} • {d.totalSpecies} species
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={shortSite}
                        size="small"
                        sx={{
                          height: '18px',
                          fontSize: '9.5px',
                          fontWeight: 700,
                          bgcolor: 'rgba(255, 255, 255, 0.06)',
                          color: '#CCC',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          ml: 1.5,
                          flexShrink: 0,
                        }}
                      />
                    </MenuItem>
                  )),
                ];
              })
            ) : (
              deployments.map((d) => (
                <MenuItem key={d.deployment_id} value={d.deployment_id}>
                  <Checkbox
                    checked={selectedDeploymentIds.includes(d.deployment_id)}
                    size="small"
                    sx={{ color: '#555', '&.Mui-checked': { color: '#F59E0B' }, mr: 1, p: 0.5 }}
                  />
                  <Typography sx={{ fontSize: '12px', color: '#EEE' }}>
                    {d.deployment_name}
                  </Typography>
                </MenuItem>
              ))
            )}
          </Select>
        </Box>

        {/* Index Metric Selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Metric:
          </Typography>
          <Select
            variant="standard"
            disableUnderline
            size="small"
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            sx={{
              color: '#F59E0B',
              fontWeight: 700,
              fontSize: '12px',
              borderBottom: '2px solid #F59E0B',
              '& .MuiSelect-select': {
                py: 0.25,
                px: 1,
              },
              '& .MuiSvgIcon-root': {
                color: '#F59E0B',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#121212',
                  border: '1px solid #282828',
                  color: '#fff',
                  '& .MuiMenuItem-root': {
                    fontSize: '12px',
                    '&.Mui-selected': { bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' },
                  },
                },
              },
            }}
          >
            {METRIC_OPTIONS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                <Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#FFF' }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: '10px', color: '#777' }}>{m.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>

          <Tooltip title="Compare acoustic soundscape indices across selected deployment nodes." arrow placement="left">
            <IconButton size="small" sx={{ color: '#666', '&:hover': { color: '#F59E0B' } }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Selected Deployments Active Tags with Parent Site Badges matching assigned series colors */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography sx={{ fontSize: '10px', color: '#777', fontWeight: 700, textTransform: 'uppercase' }}>
          Active Comparison:
        </Typography>
        {selectedDeploymentIds.map((depId, idx) => {
          const dep = deployments.find((d) => d.deployment_id === depId);
          const siteName = deploymentToSiteMap.get(depId) || 'Site';
          const color = COLOR_PALETTE[idx % COLOR_PALETTE.length];
          const depName = dep ? dep.deployment_name.split(' (')[0] : `Deployment ${idx + 1}`;

          return (
            <Chip
              key={depId}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                  <Typography sx={{ fontSize: '10px', color: color, fontWeight: 800 }}>
                    {siteName}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    //
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#FFF', fontWeight: 700 }}>
                    {depName}
                  </Typography>
                </Box>
              }
              size="small"
              onDelete={selectedDeploymentIds.length > 1 ? () => handleRemoveDeployment(depId) : undefined}
              deleteIcon={<CloseIcon sx={{ fontSize: '12px !important', color: `${color}AA`, '&:hover': { color: '#FFF' } }} />}
              sx={{
                height: '24px',
                bgcolor: `${color}14`,
                border: `1px solid ${color}66`,
                borderRadius: '6px',
                px: 0.5,
              }}
            />
          );
        })}
      </Box>

      {/* Comparison Chart */}
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </Box>
  );
};

export default DeploymentComparisonChart;
